import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { DocumentTypeCreateDto, type DocumentTypeCreateInterface } from '@zaoblako/shared/modules/document/dtos/document-type/document-type-create.dto';
import type { DocumentTypeFindInterface } from '@zaoblako/shared/modules/document/dtos/document-type/document-type-find.dto';
import { DocumentKeyEnum } from '@zaoblako/shared/modules/document/enums/document.key.enum';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DocumentTypeService } from '@zaoblako/web/core/services/finance/document-type.service';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DocumentCalculationMethodSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/document-calculation-method-single-select/document-calculation-method-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-document-type-form',
  templateUrl: './document-type-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    DocumentCalculationMethodSingleSelectComponent,
    ButtonModule,
    SpinnerComponent,
    DirectoryCommonHistoryComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentTypeFormComponent extends BaseFormComponent<DocumentTypeCreateInterface> implements OnInit {
  @Input() public readonly index: number;
  public documentType = <DocumentTypeCreateInterface>{};
  public dto = DocumentTypeCreateDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;
  public engine: 'class-transformer' | 'serde' = 'serde';

  public keys = Object.values(DocumentKeyEnum);

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  public constructor(
    public readonly documentTypeService: DocumentTypeService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.documentTypeService.findByCode(this.formStoreService.getState.forms[this.index]?.entityId as any).subscribe({
      next: (documentType) => {
        this.documentType = documentType;
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      }
    });
  }

  public saveDocumentType() {
    if (this.checkValidate(this.documentType).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.documentTypeService.updateByCode(this.documentType.code, this.documentType).subscribe({
          next: (documentType) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Тип документа "${this.documentType.name}" обновлен`);
            this.formStoreService.saveNext(documentType, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.documentTypeService.create<DocumentTypeFindInterface>(this.documentType).subscribe({
          next: (documentType) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Тип документа создан');
            this.formStoreService.saveNext(documentType, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteDocumentType() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить тип документа "${this.documentType.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.documentTypeService
          .deleteByCode(this.formStoreService.getState.forms[this.index]?.entityId as any)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Тип документа "${this.documentType.name}" удален`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }
}
